import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Ship, ArrowRight, ArrowLeft, Check, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const serviceOptions = [
  { value: 'courier', label: 'Courier' },
  { value: 'forwarder', label: 'Freight Forwarder' },
  { value: 'nvocc', label: 'NVOCC' },
  { value: 'vessel_owner', label: 'Vessel Owner' },
  { value: 'vessel_operator', label: 'Vessel Operator' },
  { value: 'shipping_line', label: 'Shipping Line' },
  { value: 'broker', label: 'Broker' },
  { value: 'customs_broker', label: 'Customs Broker' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'last_mile', label: 'Last Mile Delivery' },
  { value: 'airline_gsa', label: 'Airline GSA' },
  { value: 'trucking', label: 'Trucking' },
  { value: 'port_authority', label: 'Port Authority' },
  { value: 'port_operator', label: 'Port Operator' },
  { value: 'terminal_operator', label: 'Terminal Operator' },
  { value: 'freight_station', label: 'Freight Station / CFS' },
  { value: 'consolidator', label: 'Consolidator' }
];

const modeOptions = [
  { value: 'air', label: 'Air' },
  { value: 'sea', label: 'Sea' },
  { value: 'land', label: 'Land' },
  { value: 'multimodal', label: 'Multimodal' }
];

// Countries available for company registration
// Includes Caribbean nations and key trading partners
const availableCountries = [
  // North America
  'United States', 'Canada', 'Mexico',
  // Caribbean
  'Antigua and Barbuda', 'Bahamas', 'Barbados', 'Belize', 'Cuba', 
  'Dominica', 'Dominican Republic', 'Grenada', 'Guyana', 'Haiti', 
  'Jamaica', 'Saint Kitts and Nevis', 'Saint Lucia', 
  'Saint Vincent and the Grenadines', 'Suriname', 'Trinidad and Tobago',
  // Central America
  'Costa Rica', 'El Salvador', 'Guatemala', 'Honduras', 'Nicaragua', 'Panama',
  // South America  
  'Brazil', 'Colombia', 'Venezuela',
  // European trading partners
  'United Kingdom', 'Netherlands', 'France', 'Spain', 'Germany'
].sort();

const Register = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Step 1: User Info
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 2: Company Info
  const [legalName, setLegalName] = useState('');
  const [dba, setDba] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [taxId, setTaxId] = useState('');
  const [incorporationNumber, setIncorporationNumber] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('');

  // Step 3: Location
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // Step 4: Services
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedModes, setSelectedModes] = useState([]);
  const [certifications, setCertifications] = useState('');

  const handleServiceToggle = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const handleModeToggle = (mode) => {
    setSelectedModes(prev => 
      prev.includes(mode) 
        ? prev.filter(m => m !== mode)
        : [...prev, mode]
    );
  };

  const validateStep1 = () => {
    if (!fullName.trim() || !email.trim() || !phone.trim() || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!legalName.trim() || !country) {
      toast.error('Please fill in required fields');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!address.trim() || !city.trim()) {
      toast.error('Please fill in required fields');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return false;
    }
    if (selectedModes.length === 0) {
      toast.error('Please select at least one transport mode');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep4()) return;

    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        user_data: {
          full_name: fullName,
          email,
          phone,
          password
        },
        company_data: {
          legal_name: legalName,
          dba: dba || undefined,
          country,
          website: website || undefined,
          tax_id: taxId || undefined,
          incorporation_number: incorporationNumber || undefined,
          years_in_business: yearsInBusiness ? parseInt(yearsInBusiness) : undefined,
          services: selectedServices,
          modes: selectedModes,
          certifications: certifications ? certifications.split(',').map(c => c.trim()) : [],
          lanes_served: [],
          primary_location: {
            address,
            city,
            country,
            postal_code: postalCode || undefined,
            is_primary: true
          }
        }
      });

      toast.success('Registration successful! Please check your email to verify your account.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            s < step ? 'bg-green-500 text-white' :
            s === step ? 'bg-cyan-500 text-white' :
            'bg-slate-200 text-slate-400'
          }`}>
            {s < step ? <Check className="h-5 w-5" /> : s}
          </div>
          {s < 4 && (
            <div className={`w-16 h-1 ${
              s < step ? 'bg-green-500' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-900 via-ocean-800 to-cyan-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img 
              src="/logo-icon.png" 
              alt="CTC Logo" 
              className="h-10 w-10 object-contain rounded-lg"
            />
            <span className="text-2xl font-outfit font-bold">
              <span className="text-cyan-500">Connecting</span> The Caribbean
            </span>
          </div>
          <CardTitle className="text-2xl">Join Our Freight Network</CardTitle>
          <CardDescription>
            {step === 1 && 'Create your account'}
            {step === 2 && 'Company information'}
            {step === 3 && 'Primary location'}
            {step === 4 && 'Services & capabilities'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderStepIndicator()}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Step 1: User Info */}
            {step === 1 && (
              <>
                <div>
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    required
                    data-testid="register-fullname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="[email protected]"
                    required
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (876) 123-4567"
                    required
                    data-testid="register-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      required
                      data-testid="register-password-input"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="register-confirm-password-input"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Company Info */}
            {step === 2 && (
              <>
                <div>
                  <Label htmlFor="legalName">Company Legal Name *</Label>
                  <Input
                    id="legalName"
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    placeholder="ABC Logistics Ltd."
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dba">DBA (Doing Business As)</Label>
                  <Input
                    id="dba"
                    value={dba}
                    onChange={(e) => setDba(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Select value={country} onValueChange={setCountry} required>
                    <SelectTrigger data-testid="register-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCountries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="taxId">Tax ID</Label>
                    <Input
                      id="taxId"
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="yearsInBusiness">Years in Business</Label>
                    <Input
                      id="yearsInBusiness"
                      type="number"
                      value={yearsInBusiness}
                      onChange={(e) => setYearsInBusiness(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="incorporationNumber">Incorporation Number</Label>
                  <Input
                    id="incorporationNumber"
                    value={incorporationNumber}
                    onChange={(e) => setIncorporationNumber(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Step 3: Location */}
            {step === 3 && (
              <>
                <div>
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main Street"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input
                      id="postalCode"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                    />
                  </div>
                </div>
                <div className="text-sm text-slate-600 mt-2">
                  Country: <strong>{country}</strong>
                </div>
              </>
            )}

            {/* Step 4: Services */}
            {step === 4 && (
              <>
                <div>
                  <Label className="mb-2 block">Services Offered *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {serviceOptions.map(service => (
                      <div key={service.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={service.value}
                          checked={selectedServices.includes(service.value)}
                          onCheckedChange={() => handleServiceToggle(service.value)}
                        />
                        <label htmlFor={service.value} className="text-sm cursor-pointer">
                          {service.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Transport Modes *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {modeOptions.map(mode => (
                      <div key={mode.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={mode.value}
                          checked={selectedModes.includes(mode.value)}
                          onCheckedChange={() => handleModeToggle(mode.value)}
                        />
                        <label htmlFor={mode.value} className="text-sm cursor-pointer">
                          {mode.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="certifications">Certifications</Label>
                  <Input
                    id="certifications"
                    value={certifications}
                    onChange={(e) => setCertifications(e.target.value)}
                    placeholder="IATA, FIATA, ISO 9001 (comma-separated)"
                  />
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  data-testid="register-back-button"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              )}
              {step < 4 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="ml-auto bg-cyan-500 hover:bg-cyan-600"
                  data-testid="register-next-button"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  className="ml-auto bg-green-500 hover:bg-green-600"
                  data-testid="register-submit-button"
                >
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </Button>
              )}
            </div>
          </form>

          <div className="mt-6 text-center text-sm">
            <p className="text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-cyan-500 hover:text-cyan-600 font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;